/* eslint-disable */
import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useEffect,
} from "react";
import getLayerId from "utils/getLayerId";
import { useAuth } from "./auth";
import { NotificationManager } from "react-notifications";
import { ErrorHandelFunctionApp } from "utils/ErrorHandelFunction";

// Auth context
const esriUrlsContext = createContext();
// Setting custom name for the context which is visible on react dev tools
esriUrlsContext.displayName = "Esri Urls Context";

// reducer
function reducer(state, action) {
  switch (action.type) {
    case "INIT": {
      return { ...state, ...action.payload };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// context provider
function EsriUrlsContextProvider({ children }) {
  const initialState = { PRA_Parcels: undefined, Parcels_Intersect: undefined };

  const [auth] = useAuth();
  const { esriToken } = auth;

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);
  useEffect(() => {
    // self invoked function to get the esri urls
    (async () => {
      // serviceName
      // tableOrLayerName
      if (esriToken) {
        const services = [
          {
            serviceName: process.env.REACT_APP_PetraLayers_Table,
            // PRA_Parcels
            tableName: process.env.REACT_APP_PRA_Parcels,
            FeatureOrMapServer: "FeatureServer",
          },
          {
            serviceName: process.env.REACT_APP_DLSParcelsLayer_Table,
            // DLSParcelsLayer
            tableName: process.env.REACT_APP_PDTRA_JTM_VW,
            customName: "DLSParcelsLayer",
          },
          {
            serviceName: process.env.REACT_APP_PetraLayers_Table,
            // Parcels_Intersect
            tableName: process.env.REACT_APP_Parcels_Intersect,
          },
          {
            serviceName: process.env.REACT_APP_PetraLayers_Table,
            // TPZ_Intersect
            tableName: process.env.REACT_APP_TPZ_Intersect,
          },
          {
            serviceName: process.env.REACT_APP_PetraLayers_Table,
            // ZONING_LAND_USE_PLAN_SEGMA
            tableName: process.env.REACT_APP_ZONING_LAND_USE_PLAN_SEGMA,
          },
          {
            serviceName: process.env.REACT_APP_PetraLayers_Table,
            // NotPrintableParcels
            tableName: process.env.REACT_APP_NotPrintableParcels,
          },
          {
            serviceName: process.env.REACT_APP_PetraLayers_Table,
            // ZONING_DESC
            tableName: process.env.REACT_APP_ZONING_DESC,
          },
          {
            serviceName: process.env.REACT_APP_UserTables_Table,
            tableName: process.env.REACT_APP_EditRegulationsRequests,
            FeatureOrMapServer: "FeatureServer",
          },
          {
            serviceName: process.env.REACT_APP_UserTables_Table,
            tableName: process.env.REACT_APP_RegulationsPrice,
            FeatureOrMapServer: "FeatureServer",
          },
          {
            serviceName: process.env.REACT_APP_UserTables_Table,
            tableName: process.env.REACT_APP_ZoningRequests,
            FeatureOrMapServer: "FeatureServer",
          },
          {
            serviceName: process.env.REACT_APP_UserTables_Table,
            tableName: process.env.REACT_APP_RequestParcelsRel,
            FeatureOrMapServer: "FeatureServer",
          },
          {
            serviceName: process.env.REACT_APP_UserTables_Table,
            // ZONING_DESC
            tableName: process.env.REACT_APP_ZONING_DESC,
            customName: "changeableZoningDesc",
          },
          {
            serviceName: process.env.REACT_APP_UserTables_Table,
            // featureParcelsIntersect
            tableName: process.env.REACT_APP_Parcels_Intersect,
            customName: "featureParcelsIntersect",
          },
          {
            serviceName: process.env.REACT_APP_DLS_Lockups_Table,
            tableName: process.env.REACT_APP_HODE_LKP,
            customName: "hodesLookUp",
          },
          {
            serviceName: process.env.REACT_APP_DLS_Lockups_Table,
            tableName: process.env.REACT_APP_SECT_LKP,
            customName: "sectorsLookup",
          },
          {
            serviceName: process.env.REACT_APP_DLS_Lockups_Table,
            tableName: process.env.REACT_APP_VILL_LKP,
            customName: "villagesLookup",
          },
        ];
        const layerIdsReq = services.map((service) =>
          getLayerId(service["serviceName"], service["tableName"], {
            token: esriToken,
            MapOrFeature: service["FeatureOrMapServer"] || "MapServer",
          })
        );
        let layerIdsRes;
        try {
          layerIdsRes = await Promise.all(layerIdsReq);
        } catch (error) {
          ErrorHandelFunctionApp(error)
        }
        const layers = {};
        layerIdsRes.map((id, index) => {
          if (services[index]["customName"]) {
            return (layers[services[index]["customName"]] = id);
          }
          return (layers[services[index]["tableName"]] = id);
        });
        dispatch({ type: "INIT", payload: layers });
      }
    })();
  }, [esriToken]);

  return (
    <esriUrlsContext.Provider value={value}>
      {children}
    </esriUrlsContext.Provider>
  );
}

// custom hook for using context
function useEsriUrls() {
  const context = useContext(esriUrlsContext);

  if (!context) {
    throw new Error(
      "useEsriUrls should be used inside the EsriUrlsContextProvider."
    );
  }

  return context;
}

// Context module functions

export { EsriUrlsContextProvider, useEsriUrls };
